import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Modal,
  Card,
  CardContent,
  alpha,
  useTheme
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import {
  Search as SearchIcon,
  ClearAll,
  CalendarToday,
  Warning
} from '@mui/icons-material';
import { httpRequest } from 'src/services/httpRequest';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';
import { addCommaWithTwoDecimals } from 'src/services/common';

// Types
interface Bid {
  bidId: string;
  bidType: string;
  title: string;
  category: string;
  adDate: string;
  openingDate: string;
  status: string;
  awardedTo?: string;
  uploadedFiles: number;
  departmentId: string;
  ContractNo: string;
  description?: string;
  documents?: {
    id: number;
    name: string;
    type: string;
    description?: string;
    documentUrl: string;
  }[];
  awardedToVendor?: any;
  awardAmount?: string;
  isAddendum: boolean;
  childItems?: Bid[];
}

interface SearchFilters {
  title: string;
  contractNo: string;
  awardedTo: string;
  adDate: Date | null;
}

interface BidResponse {
  AwardedToVendor: any;
  BidID: number;
  TypeID: number;
  ContractNo: string;
  Title: string;
  CategoryIDs: number[];
  DepartmentID: number;
  GoLiveDate: string;
  BidDeadLineDate: string;
  AdDate: string;
  EffectiveDate: string;

  OpeningDate: string;
  AwardDate: string | null;
  StatusID: number;
  Description: string | null;
  AdminNote: string | null;
  IsPublish: boolean;
  ReasonToReopen: string | null;
  AwardedTo: string | null;
  ReasonToReject: string | null;
  TownshipID: number;
  Active: boolean;
  RowStatus: number;
  CreateDate: string;
  CreatedBy: number;
  UpdateDate: string;
  UpdatedBy: number;
  DeleteDate: string | null;
  DeletedBy: number | null;
  Categories: {
    CategoryID: number;
    CategoryName: string;
  }[];
  Status: string;
  Type: string;
  Documents: {
    BidDocsID: number;
    DocumentName: string;
    DocumentType: string;
    DocumentDescription: string;
    DocumentUrl: string;
  }[];
  AwardedToContact?: {
    Name: string;
    Email: string;
    Phone: string;
  };
  AwardAmount?: number;
  IsAddendum: boolean;
  ChildBidDocuments: {
    ChildBidDocsID: number;
    DocumentName: string;
    DocumentType: string;
    DocumentDescription: string;
    DocumentUrl: string;
    InsertDate: string;
  }[];
}

interface APIResponse {
  status: string;
  message: string;
  data: {
    TotalRecords: number;
    CurrentPage: number;
    TotalPages: number;
    Data: BidResponse[];
  };
}

const MyContracts: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  // State management
  const [filters, setFilters] = useState<SearchFilters>({
    title: '',
    contractNo: '',
    awardedTo: '',
    adDate: null
  });

  const { state: locationState, township, uuid } = useParams();
  const [bids, setBids] = useState<Bid[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showTerms, setShowTerms] = useState(false);

  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  const fetchBidsWithFilters = async (
    pageNum: number,
    pageSize: number,
    searchFilters: SearchFilters = filters
  ) => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        Page: (pageNum + 1)?.toString(),
        Size: pageSize?.toString(),
        ...(searchFilters.title && { Title: searchFilters.title }),

        ...(searchFilters.adDate && {
          EffectiveDate: searchFilters.adDate.toISOString()
        }),

        ...(searchFilters.contractNo && {
          ContractNo: searchFilters.contractNo
        })
      });

      const response = (await httpRequest(
        'GET',
        `/bid_portal/bid_details/myContracts?${queryParams.toString()}`
      )) as APIResponse;

      if (response.status === 'success') {
        const transformedBids: any[] = response?.data?.Data?.map((bid) => ({
          bidId: bid?.BidID.toString(),
          bidType: bid?.Type,
          title: bid?.Title,
          category: bid?.Categories?.map((cat) => cat?.CategoryName)?.join(
            ', '
          ),
          adDate: bid?.EffectiveDate,
          openingDate: bid?.OpeningDate,
          status: bid?.Status,
          awardedTo: bid?.AwardedTo || '',
          uploadedFiles: bid?.Documents?.length,
          departmentId: bid?.DepartmentID?.toString(),
          ContractNo: bid?.ContractNo,
          description: bid?.Description || '',
          awardedToVendor: bid?.AwardedTo || null,
          awardAmount: bid?.AwardAmount,
          awardedToContact: bid?.AwardedToContact
            ? {
                name: bid?.AwardedToContact.Name,
                email: bid?.AwardedToContact.Email,
                phone: bid?.AwardedToContact.Phone
              }
            : null,
          documents: bid?.Documents?.map((doc) => ({
            id: doc.BidDocsID,
            name: doc.DocumentName,
            type: doc.DocumentType,
            description: doc.DocumentDescription,
            documentUrl: doc.DocumentUrl
          })),
          isAddendum: bid?.IsAddendum,
          childItems: [],
          childBidDocuments: bid?.ChildBidDocuments
        }));

        setBids(transformedBids);
        setTotalCount(response.data.TotalRecords);
      } else {
        toast.error(response.message || 'Failed to fetch contracts');
      }
    } catch (error) {
      console.error('Error fetching contracts:', error);
      toast.error('Failed to fetch contracts. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const fetchBids = async (pageNum: number, pageSize: number) => {
    await fetchBidsWithFilters(pageNum, pageSize);
  };

  useEffect(() => {
    fetchBids(page, rowsPerPage);
  }, [page, rowsPerPage]);

  const handleSearch = () => {
    setPage(0);
    fetchBidsWithFilters(0, rowsPerPage);
  };
  const handleClear = () => {
    // Create the cleared filters object
    const clearedFilters = {
      title: '',
      categories: [],
      statuses: [],
      bidTypes: [],
      adDate: null,
      openingDate: null,
      contractNo: '',
      awardedTo: ''
    };

    // Update the state
    setFilters(clearedFilters);
    setPage(0);

    fetchBidsWithFilters(0, rowsPerPage, clearedFilters);
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box
      sx={{
        p: 3,
        background: alpha(theme.palette.background.default, 0.8),
        minHeight: '100vh'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 4
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: 'bold',
            color: theme.palette.primary.main,
            position: 'relative',
            '&:after': {
              content: '""',
              position: 'absolute',
              bottom: -8,
              left: 0,
              width: 105,
              height: 4,
              borderRadius: 2,
              backgroundColor: theme.palette.primary.main
            }
          }}
        >
          My Contracts
        </Typography>
      </Box>

      <Card
        sx={{
          mb: 4,
          borderRadius: 2,
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          overflow: 'hidden',
          border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`
        }}
      >
        <Box
          sx={{
            p: 2.5,
            background: `linear-gradient(to right, ${alpha(
              theme.palette.primary.main,
              0.05
            )}, ${alpha(theme.palette.primary.main, 0.02)})`,
            borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
          }}
        >
          <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
            <SearchIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Search Filters
          </Typography>
        </Box>
        <CardContent sx={{ p: 3 }}>
          <Grid container spacing={2.5}>
            {/* First row - 3 fields */}
            <Grid item xs={12} sm={4} md={4}>
              <TextField
                size="small"
                fullWidth
                label="Title"
                variant="outlined"
                value={filters.title}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    title: e.target.value
                  })
                }
                InputProps={{
                  sx: {
                    borderRadius: 1.5,
                    height: 40,
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: alpha(theme.palette.primary.main, 0.5)
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderWidth: 2
                    }
                  }
                }}
                sx={{
                  '& .MuiInputLabel-root': {
                    color: theme.palette.text.secondary,
                    fontSize: '0.875rem',
                    transform: 'translate(14px, 11px) scale(1)'
                  },
                  '& .MuiInputLabel-shrink': {
                    transform: 'translate(14px, -6px) scale(0.75)'
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    fontWeight: 500
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} sm={4} md={4}>
              <TextField
                size="small"
                fullWidth
                label="Contract No"
                variant="outlined"
                value={filters.contractNo}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    contractNo: e.target.value
                  })
                }
                InputProps={{
                  sx: {
                    borderRadius: 1.5,
                    height: 40,
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: alpha(theme.palette.primary.main, 0.5)
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderWidth: 2
                    }
                  }
                }}
                sx={{
                  '& .MuiInputLabel-root': {
                    color: theme.palette.text.secondary,
                    fontSize: '0.875rem',
                    transform: 'translate(14px, 11px) scale(1)'
                  },
                  '& .MuiInputLabel-shrink': {
                    transform: 'translate(14px, -6px) scale(0.75)'
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    fontWeight: 500
                  }
                }}
              />
            </Grid>

            {/* Second row with date picker */}
            <Grid item xs={12} sm={4} md={4}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Effective Date"
                  value={filters.adDate}
                  onChange={(date) => setFilters({ ...filters, adDate: date })}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'small',
                      variant: 'outlined',
                      InputProps: {
                        startAdornment: (
                          <CalendarToday
                            sx={{
                              color: theme.palette.text.secondary,
                              mr: 1,
                              fontSize: '1rem'
                            }}
                          />
                        ),
                        sx: {
                          borderRadius: 1.5,
                          height: 40,
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: alpha(theme.palette.primary.main, 0.5)
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderWidth: 2
                          }
                        }
                      },
                      sx: {
                        '& .MuiInputLabel-root': {
                          color: theme.palette.text.secondary,
                          fontSize: '0.875rem',
                          transform: 'translate(14px, 11px) scale(1)'
                        },
                        '& .MuiInputLabel-shrink': {
                          transform: 'translate(14px, -6px) scale(0.75)'
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          fontWeight: 500
                        }
                      }
                    },
                    desktopPaper: {
                      sx: {
                        borderRadius: 2,
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                        '& .MuiPickersDay-root.Mui-selected': {
                          backgroundColor: theme.palette.primary.main,
                          '&:hover': {
                            backgroundColor: theme.palette.primary.dark
                          }
                        }
                      }
                    }
                  }}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12}>
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  justifyContent: 'flex-end',
                  height: '100%',
                  alignItems: 'center'
                }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSearch}
                  startIcon={<SearchIcon />}
                  size="small"
                  sx={{
                    borderRadius: 1.5,
                    px: 3,
                    py: 0.8,
                    fontWeight: 'bold',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      boxShadow: '0 4px 10px rgba(0, 0, 0, 0.18)',
                      transform: 'translateY(-2px)'
                    }
                  }}
                >
                  Search
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleClear}
                  startIcon={<ClearAll />}
                  size="small"
                  sx={{
                    borderRadius: 1.5,
                    px: 3,
                    py: 0.8,
                    fontWeight: 'bold',
                    borderWidth: '1px',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderWidth: '1px',
                      backgroundColor: alpha(theme.palette.primary.main, 0.04)
                    }
                  }}
                >
                  Clear
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Paper
        sx={{
          borderRadius: 2,
          overflow: 'hidden',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`
        }}
      >
        <Box
          sx={{
            p: 2.5,
            background: `linear-gradient(to right, ${alpha(
              theme.palette.primary.main,
              0.05
            )}, ${alpha(theme.palette.primary.main, 0.02)})`,
            borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
            Contracts
          </Typography>
          {!loading && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontWeight: 500 }}
            >
              Total Results: {totalCount}
            </Typography>
          )}
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table
                sx={{
                  '& .MuiTableCell-head': {
                    bgcolor: alpha(theme.palette.primary.main, 0.03),
                    fontWeight: 'bold',
                    color: theme.palette.text.primary,
                    py: 2
                  },
                  '& .MuiTableCell-body': {
                    py: 1.8
                  }
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>Contract No.</TableCell>
                    <TableCell>Title</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Effective Date</TableCell>
                    <TableCell>Awarded To</TableCell>
                    <TableCell align="center">Award Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bids?.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} align="center" sx={{ py: 5 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 2
                          }}
                        >
                          <Warning
                            sx={{
                              fontSize: 40,
                              color: alpha(theme.palette.text.secondary, 0.6)
                            }}
                          />
                          <Typography
                            variant="body1"
                            color="text.secondary"
                            sx={{ fontWeight: 500 }}
                          >
                            No contracts found. Try adjusting your search
                            criteria.
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    bids?.map((bid) => (
                      <>
                        <TableRow
                          key={bid.bidId}
                          hover
                          sx={{
                            '&:hover': {
                              bgcolor: alpha(theme.palette.primary.main, 0.03)
                            },
                            transition: 'background-color 0.2s ease',
                            '&:nth-of-type(even)': {
                              bgcolor: alpha(
                                theme.palette.background.default,
                                0.4
                              )
                            }
                          }}
                        >
                          <TableCell sx={{ fontWeight: 500 }}>
                            {bid.ContractNo || '-'}
                          </TableCell>

                          <TableCell
                            sx={{
                              maxWidth: 220,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {bid.title || '-'}
                          </TableCell>
                          <TableCell
                            sx={{
                              maxWidth: 220,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {bid.description || '-'}
                          </TableCell>
                          <TableCell>
                            {bid?.adDate
                              ? new Date(bid.adDate).toLocaleDateString('en-US')
                              : '-'}
                          </TableCell>

                          <TableCell
                            sx={{
                              maxWidth: 180,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {bid?.awardedToVendor || '-'}
                          </TableCell>

                          <TableCell align="center" sx={{ fontWeight: 500 }}>
                            {bid.awardAmount ? `$${addCommaWithTwoDecimals(bid.awardAmount)}` : '-'}
                          </TableCell>
                        </TableRow>
                      </>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Box
              sx={{
                borderTop: `1px solid ${alpha(
                  theme.palette.primary.main,
                  0.1
                )}`,
                bgcolor: alpha(theme.palette.background.paper, 0.5)
              }}
            >
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50, 100]}
                sx={{
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows':
                    {
                      fontWeight: 500
                    },
                  '& .MuiTablePagination-select': {
                    borderRadius: 1.5,
                    '&:focus': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.05)
                    }
                  },
                  '& .MuiTablePagination-actions': {
                    '& .MuiIconButton-root': {
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.1)
                      }
                    }
                  }
                }}
              />
            </Box>
          </>
        )}
      </Paper>

      {/* Terms and Conditions Modal */}
      <Modal
        open={showTerms}
        onClose={() => setShowTerms(false)}
        aria-labelledby="terms-modal-title"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '70%',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            maxHeight: '80vh',
            overflow: 'auto',
            borderRadius: 2
          }}
        >
          <Typography variant="h6" id="terms-modal-title" sx={{ mb: 2 }}>
            Terms Of Download
          </Typography>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            Login is required
          </Typography>
          <Typography paragraph>
            It is the responsibility of the Vendor to ensure all pages of the
            bid package have been downloaded and printed properly.
          </Typography>
          <Box
            sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 3 }}
          >
            <Button
              variant="contained"
              color="success"
              onClick={() =>
                navigate(`/${locationState}/${township}/${uuid}/login`)
              }
              sx={{
                borderRadius: 1.5,
                px: 3,
                py: 1,
                fontWeight: 'bold',
                boxShadow: '0 4px 10px rgba(0, 0, 0, 0.15)',
                '&:hover': {
                  boxShadow: '0 6px 12px rgba(0, 0, 0, 0.18)'
                }
              }}
            >
              Agree & Proceed
            </Button>
            <Button
              variant="outlined"
              onClick={() => setShowTerms(false)}
              sx={{
                borderRadius: 1.5,
                px: 3,
                py: 1,
                fontWeight: 'medium',
                borderWidth: '1.5px',
                '&:hover': {
                  borderWidth: '1.5px',
                  backgroundColor: alpha(theme.palette.primary.main, 0.04)
                }
              }}
            >
              Disagree
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default MyContracts;
